import { useEffect, useState, useCallback } from "react";
import { apiHandler } from "../../../api";
import { endpoint } from "../../../api/endpoint";
import { useDispatch, useSelector } from "react-redux";
import { useToasts } from "react-toast-notifications";

export const useCounterPartyIssueDetails = ({ issueId, onBack }) => {
  const { addToast } = useToasts();
  const [issueData, setIssueData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState("requestInfo"); // "requestInfo" | "provideResolution"
  const dispatch = useDispatch();
  const { authData, storeId } = useSelector((state) => state.loginSlice);
  const [infoRequestType, setInfoRequestType] = useState("new"); // "new" | "refer"
  const [selectedPendingRefId, setSelectedPendingRefId] = useState("");

  // Form state
  const [formData, setFormData] = useState({
    ref_desc: "",
    short_desc: "",
    long_desc: "",
    resolutions: [
      {
        ref_type: "ITEM",
        ref_id: "I1",
        resolutions: [
          { type: "REFUND", short_desc: "", amount: "", expected_delivery: "" },
        ],
      },
      {
        ref_type: "ITEM",
        ref_id: "I2",
        resolutions: [
          { type: "REFUND", short_desc: "", amount: "", expected_delivery: "" },
        ],
      },
    ],
  });

  const closeModal = () => setIsModalOpen(false);

  const getIssueDetails = useCallback(async () => {
    if (!issueId) return;
    try {
      setLoading(true);
      setError(null);

      const result = await apiHandler({
        url: `${endpoint.GET_ISSUE_DETAILS}?id=${issueId}`,
        method: "GET",
        headers: { "Content-Type": "application/json" },
        authToken: authData,
        dispatch,
        storeId,
      });

      setIssueData(result?.data || null);
    } catch (err) {
      console.error("Error fetching issue details:", err);
      setError(err.message || "Failed to fetch issue details");
      setIssueData(null);
    } finally {
      setLoading(false);
    }
  }, [issueId, authData, dispatch, storeId]);

  useEffect(() => {
    getIssueDetails();
  }, [getIssueDetails]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleResolutionChange = (itemIdx, resIdx, field, value) => {
    const newData = [...formData.resolutions];
    newData[itemIdx].resolutions[resIdx][field] = value;
    setFormData({ ...formData, resolutions: newData });
  };

  const handleAddResolution = (itemIdx) => {
    const currentItemResolutions = formData.resolutions[itemIdx].resolutions;

    if (currentItemResolutions.length >= 3) {
      addToast("You can only provide a maximum of 3 resolutions per item.", {
        appearance: "error",
        autoDismiss: true,
      });
      return;
    }

    const newData = [...formData.resolutions];
    newData[itemIdx].resolutions.push({
      type: "",
      short_desc: "",
      amount: "",
      expected_delivery: "",
    });
    setFormData({ ...formData, resolutions: newData });
  };

  const handleRemoveResolution = (itemIdx, resIdx) => {
    const newData = [...formData.resolutions];
    newData[itemIdx].resolutions.splice(resIdx, 1);
    setFormData({ ...formData, resolutions: newData });
  };

  const handleSave = async ({ onClose }) => {
    try {
      setLoading(true);

      if (modalType === "requestInfo") {
        if (!formData.short_desc || formData.short_desc.trim() === "") {
          addToast("Short Description is required!", {
            appearance: "error",
            autoDismiss: true,
          });
          setLoading(false);
          return;
        }
      }
      let payload, url;

      if (modalType === "requestInfo") {
        url = `${endpoint.REQUEST_INFO}/${issueId}`;
        payload = {
          short_desc: formData.short_desc,
          long_desc: formData.long_desc,
          ...(infoRequestType === "refer" && selectedPendingRefId
            ? { ref_id: selectedPendingRefId }
            : {}),
        };
      } else if (modalType === "provideResolution") {
        url = `${endpoint.PROVIDE_RESOLUTION}/${issueId}`;
        payload = {
          proposed_resolutions: formData.resolutions.map((resGroup) => ({
            ref_type: resGroup.ref_type,
            ref_id: resGroup.ref_id,
            resolutions: resGroup.resolutions.map((r) => ({
              type: r.type,
              short_desc: r.short_desc,
              amount: r.type === "REFUND" && r.amount ? Number(r.amount) : null,
              expected_delivery:
                (r.type === "NO_ACTION" || r.type === "REPLACEMENT") &&
                r.expected_delivery
                  ? r.expected_delivery
                  : null,
            })),
          })),
        };
      }

      const response = await apiHandler({
        url,
        method: "POST",
        data: payload,
        headers: { "Content-Type": "application/json" },
        authToken: authData,
        dispatch,
        storeId,
      });

      // Determine success/message depending on modal type
      const result =
        modalType === "provideResolution"
          ? response?.data?.result
          : response?.data;

      if (result?.success) {
        addToast(result?.message || "Success", {
          appearance: "success",
          autoDismiss: true,
        });
        // Reset form
        setFormData({ short_desc: "", long_desc: "", resolutions: [] });
        onClose?.();
        getIssueDetails();
      } else {
        addToast(result?.message || "Failed to submit", {
          appearance: "error",
          autoDismiss: true,
        });
      }
    } catch (err) {
      console.error("Save error:", err);
      addToast("Something went wrong. Please try again.", {
        appearance: "error",
        autoDismiss: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const resolveIssue = async () => {
    try {
      const result = await apiHandler({
        url: `${endpoint.RESOLVE_ISSUE}/${issueId}`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        authToken: authData,
        dispatch,
        storeId,
      });
      console.log(result);

      if (result?.data?.success) {
        addToast(result?.message || "Resolve Issue status sent successfully", {
          appearance: "success",
          autoDismiss: true,
        });
        // Reset form
      } else {
        addToast(result?.message || "Failed to resovle", {
          appearance: "error",
          autoDismiss: true,
        });
      }

      // setIssueData(result?.data || null);
    } catch (err) {
      console.error("Error fetching issue details:", err);
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      // Default back behavior
      window.history.back();
    }
  };

  const handleBellClick = () => {
    setModalType("viewInfoRequests");
    setIsModalOpen(true);
  };

  return {
    issueData,
    loading,
    error,
    refetch: getIssueDetails,
    formData,
    setFormData,
    handleChange,
    handleResolutionChange,
    handleSave,
    isModalOpen,
    setIsModalOpen,
    modalType,
    setModalType,
    closeModal,
    handleBack,
    resolutionDropdowns: issueData?.Resolution_dropdowns,
    handleAddResolution,
    handleRemoveResolution,
    handleBellClick,
    infoRequestType,
    setInfoRequestType,
    selectedPendingRefId,
    setSelectedPendingRefId,
    resolveIssue,
  };
};
